import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, RefreshCw, Search, Ban } from "lucide-react";
import { useFetch } from "../../lib/useFetch";
import { api, errMsg, eur, fmtDate } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { AdminH } from "./AdminLayout";

const STATE = { active: "bg-emerald-100 text-emerald-700", used: "bg-slate-200 text-slate-600", expired: "bg-amber-100 text-amber-700", revoked: "bg-rose-100 text-rose-700" };

const NewDiscount = ({ onClose, onDone }) => {
  const [code, setCode] = useState(null);
  const [kind, setKind] = useState("percent");
  const [value, setValue] = useState("");
  const [q, setQ] = useState("");
  const [userSel, setUserSel] = useState(null);
  const [eventId, setEventId] = useState("");
  const { data: events } = useFetch("/admin/events");
  const today = new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Rome" });
  const open = (events || []).filter((e) => e.status === "Registrations open" && (e.reg_deadline || e.start_date) >= today);
  const ev = open.find((e) => e.id === eventId);
  const { data: users } = useFetch(q.length >= 2 ? `/admin/users?q=${encodeURIComponent(q)}` : null);
  const gen = () => api.get("/admin/discounts/new-code").then((r) => setCode(r.data.code));
  useEffect(() => { gen(); }, []);
  const send = async () => {
    try {
      const { data } = await api.post("/admin/discounts", { kind, value: Number(value), user_id: userSel.id, code, event_id: eventId });
      toast.success(`Code ${data.code} created and emailed to ${data.email} (${data.email_delivery})`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-3xl" data-testid="discount-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">New discount</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div className="flex items-center justify-between rounded-2xl bg-[#0A2540] p-4 text-white">
            <span className="font-mono text-2xl tracking-[0.3em]" data-testid="discount-code-preview">{code || "········"}</span>
            <button onClick={gen} className="text-white/70 hover:text-white" title="Generate another" data-testid="discount-regenerate"><RefreshCw size={18} /></button>
          </div>
          <p className="-mt-3 text-xs text-slate-500">Random 8-character code (this is the code that will be sent). Valid once. Applies only to the event participation fee (net), not to charter or services.</p>
          <label className="block text-xs font-semibold text-slate-600">Linked event (optional)
            <select value={eventId} onChange={(e) => setEventId(e.target.value)} className="mt-1 h-11 w-full rounded-xl border border-slate-200 px-3 text-sm font-normal" data-testid="discount-event">
              <option value="">Any clinic – expires 60 days after creation</option>
              {open.map((e) => <option key={e.id} value={e.id}>{e.title} · {fmtDate(e.start_date)}</option>)}
            </select>
          </label>
          {ev && <p className="-mt-3 text-xs text-[#00A8B5]" data-testid="discount-event-note">Valid only for one registration fee (one participant) of this event, until the registration deadline {fmtDate(ev.reg_deadline || ev.start_date)}.</p>}
          <div className="flex gap-2">
            {[["percent", "Percentage %"], ["fixed", "Fixed amount €"]].map(([k, l]) => (
              <button key={k} onClick={() => setKind(k)} data-testid={`discount-kind-${k}`} className={`flex-1 rounded-2xl border-2 p-3 text-sm font-semibold ${kind === k ? "border-[#FF5722] bg-orange-50 text-[#0A2540]" : "border-slate-200 text-slate-500"}`}>{l}</button>
            ))}
          </div>
          <Input type="number" min="0" step="0.01" value={value} onChange={(e) => setValue(e.target.value)} placeholder={kind === "percent" ? "e.g. 10 (%)" : "e.g. 50 (€)"} className="h-11 rounded-xl" data-testid="discount-value" />
          <div>
            <div className="relative"><Search size={16} className="absolute left-3 top-3.5 text-slate-400" />
              <Input value={q} onChange={(e) => { setQ(e.target.value); setUserSel(null); }} placeholder="Search recipient by name" className="h-11 rounded-xl pl-9" data-testid="discount-user-search" /></div>
            {userSel ? <p className="mt-2 rounded-xl bg-cyan-50 p-3 text-sm" data-testid="discount-user-selected"><b>{userSel.first_name} {userSel.last_name}</b> · {userSel.email}</p>
              : (users || []).slice(0, 6).map((u) => (
                <button key={u.id} onClick={() => setUserSel(u)} className="mt-1 block w-full rounded-xl px-3 py-2 text-left text-sm hover:bg-slate-50" data-testid={`discount-user-option-${u.id}`}>{u.first_name} {u.last_name} · <span className="text-slate-500">{u.email}</span></button>))}
          </div>
          <button onClick={send} disabled={!userSel || !(Number(value) > 0)} className="btn-buoy w-full justify-center disabled:opacity-50" data-testid="discount-send">Create & send by email</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminDiscounts() {
  const { data, reload } = useFetch("/admin/discounts");
  const [open, setOpen] = useState(false);
  const revoke = async (d) => {
    if (!window.confirm(`Revoke code ${d.code}?`)) return;
    try { await api.delete(`/admin/discounts/${d.id}`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="admin-discounts">
      <AdminH title="Discounts" sub="Single-use codes sent by email to a user. They apply to the event participation fee only. Codes linked to an event are valid for that event only, until its registration deadline; the others expire after 60 days.">
        <button onClick={() => setOpen(true)} className="btn-buoy" data-testid="discount-new"><Plus size={16} />New discount</button>
      </AdminH>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr>{["Code", "Discount", "Recipient", "Created", "Expires", "Status", ""].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(data || []).map((d) => (
              <tr key={d.id} data-testid={`discount-row-${d.id}`}>
                <td className="px-4 py-3 font-mono font-semibold tracking-widest">{d.code}</td>
                <td className="px-4 py-3">{d.kind === "percent" ? `${d.value}%` : eur(d.value)}{d.event_title && <span className="block text-xs text-[#00A8B5]" data-testid={`discount-event-${d.id}`}>{d.event_title}</span>}</td>
                <td className="px-4 py-3">{d.user_name}<span className="block text-xs text-slate-500">{d.email}</span></td>
                <td className="px-4 py-3">{fmtDate(d.created_at)}</td><td className="px-4 py-3">{fmtDate(d.expires_at)}</td>
                <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${STATE[d.state]}`} data-testid={`discount-state-${d.id}`}>{d.state}{d.used_at ? ` · ${fmtDate(d.used_at)}` : ""}</span></td>
                <td className="px-4 py-3 text-right">{d.state === "active" && <button onClick={() => revoke(d)} className="text-slate-400 hover:text-rose-600" title="Revoke" data-testid={`discount-revoke-${d.id}`}><Ban size={16} /></button>}</td>
              </tr>
            ))}
            {data && !data.length && <tr><td colSpan={7} className="px-4 py-6 text-slate-500">No discounts yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {open && <NewDiscount onClose={() => setOpen(false)} onDone={() => { setOpen(false); reload(); }} />}
    </div>
  );
}
